import { takeEvery } from 'redux-saga/effects';

import type { SimpleRequestConfig } from 'modules/app/types';
import type { ApiError, ApiFailureAction, EmptyResponseBody, MetaAction } from 'modules/global/generic.types';
import * as actions from 'modules/kiosks/actions';
import * as types from 'modules/kiosks/actionTypes';
import type {
  DeleteKioskAction,
  DetailedKiosk,
  DetailedKiosksPage,
  FetchKiosksByPaymentTypesAction,
  KiosksPage,
  PatchKioskStatusAction,
} from 'modules/kiosks/types';

const HTTP_UNPROCESSABLE_ENTITY = 422;
const HTTP_NOT_FOUND = 404;

const GENERIC_FAILURE_NOTIFICATION = {
  id: 'http.error.message',
  defaultMessage: 'Oops! Something went wrong.',
};

const SILENT_FAILURE = { failure: () => undefined };

const simpleRequests: Record<string, SimpleRequestConfig> = {
  [types.PUT_KIOSK]: {
    callbacks: {
      success: (data: DetailedKiosk, meta: MetaAction) => actions.updateKioskSuccess(data, meta),
      failure: (error: ApiError, meta: MetaAction) => actions.updateKioskFailure(error, meta),
    },
    notifications: SILENT_FAILURE,
  },
  [types.POST_KIOSK]: {
    callbacks: {
      success: (data: DetailedKiosk, meta: MetaAction) => actions.createKioskSuccess(data, meta),
      failure: (error: ApiError, meta: MetaAction) => actions.createKiosksFailure(error, meta),
    },
    notifications: SILENT_FAILURE,
  },
  [types.FETCH_KIOSK]: {
    callbacks: {
      success: (data: DetailedKiosk, meta: MetaAction) => actions.fetchKioskSuccess(data, meta),
      failure: (error: ApiError, meta: MetaAction) => actions.fetchKioskFailure(error, meta),
    },
    notifications: SILENT_FAILURE,
  },
  [types.FETCH_KIOSKS]: {
    callbacks: {
      success: (data: KiosksPage, meta: MetaAction) => actions.fetchKiosksSuccess(data, meta),
      failure: (error: ApiError, meta: MetaAction) => actions.fetchKiosksFailure(error, meta),
    },
    notifications: SILENT_FAILURE,
  },
  [types.DELETE_REQUEST]: {
    callbacks: {
      success: (_: EmptyResponseBody, meta: MetaAction, { payload }: DeleteKioskAction) =>
        actions.deleteKioskSuccess(payload.kioskId, meta),
      failure: (error: ApiError, meta: MetaAction, { payload }: DeleteKioskAction) =>
        actions.deleteKioskFailure(error, { ...meta, entityId: payload.kioskId }),
    },
    notifications: {
      success: { id: 'page.kiosks.delete.success', defaultMessage: 'The kiosk has been deleted with success.' },
      failure: { id: 'page.kiosks.delete.failure', defaultMessage: 'An error occurred during the kiosk deletion.' },
    },
  },
  [types.PATCH_STATUS_REQUEST]: {
    callbacks: {
      failure: (error: ApiError, meta: MetaAction, { payload }: PatchKioskStatusAction) =>
        actions.patchKioskStatusFailure(error, { ...meta, entityId: payload.kioskId }),
    },
    notifications: {
      success: { id: 'page.kiosks.update.success', defaultMessage: 'The kiosk has been updated with success.' },
      failure: { id: 'page.kiosks.update.failure', defaultMessage: 'An error occurred during the kiosk update.' },
    },
  },
  [types.FETCH_KIOSKS_BY_PAYMENT_TYPES]: {
    effect: takeEvery,
    callbacks: {
      success: (data: DetailedKiosksPage, meta: MetaAction, { payload }: FetchKiosksByPaymentTypesAction) =>
        actions.getKiosksByPaymentTypesSuccess(payload.dealerId, data, meta),
    },
  },
  [types.CONFIGURE_REQUEST]: {
    notifications: {
      success: { id: 'page.kiosks.update.success', defaultMessage: 'The kiosk has been updated with success.' },
    },
  },
  [types.RESET_KIOSK_STATUS]: {
    notifications: {
      success: {
        id: 'interventionKanban.masterPinModal.resetSuccess',
        defaultMessage: 'The kiosk has been successfully reset.',
      },
      failure: ({ payload }: ApiFailureAction) =>
        (payload.response?.status === HTTP_UNPROCESSABLE_ENTITY
          ? {
            id: 'interventionKanban.masterPinModal.resetTooSoon',
            defaultMessage: 'A delay of 5 minutes is required before the kiosk can be reset again.',
          }
          : GENERIC_FAILURE_NOTIFICATION),
    },
  },
  [types.CHECK_KIOSK_LOCATION_ID_REQUEST]: {
    notifications: {
      failure: ({ payload }: ApiFailureAction) =>
        (payload.response?.status === HTTP_NOT_FOUND ? undefined : GENERIC_FAILURE_NOTIFICATION),
    },
  },
};

export default simpleRequests;
